import {
  Assoc,
  AssocEntry,
  AttrAccess,
  Call,
  Closure,
  EraQlAst,
  Let,
  List,
} from '../eraql-ast/ast';
import {
  Head,
  IfWrapAncestor,
  Indent,
  Line,
  LineOrNothing,
  FormatNode,
  Nodes,
  Text,
} from './Node';
import { v4 } from 'uuid';
import { Assert, assertExists } from '@cotera/utilities';
import { buildInfixOperatorCall } from './build-infix-operator-call';
import { LiteralToken } from '../lex/tokens';
import { INFIX_OPERATOR_TO_FC } from '../special-forms/expr/infix-mappings';
import { escapeStructKey } from '../to-eraql/helpers';
import { WithComments } from '../lex/types';

/**
 * Builds a formatting tree from any kind of EraQLAst node.
 */
export function buildNode(
  astNodeWithComments: WithComments<EraQlAst>
): FormatNode {
  const [astNode, meta] = astNodeWithComments;
  switch (astNode.t) {
    case 'symbol':
      return Text(astNode.sym);
    case 'literal':
      return buildLiteral([astNode, meta]);
    case 'ident':
      return Text(`"${astNode.name}"`);
    case 'call':
      return buildCall(astNode);
    case 'assoc':
      return buildAssoc(astNode);
    case 'list':
      return buildList(astNode);
    case 'let':
      return buildLet([astNode, meta]);
    case 'attr-access':
      return buildAttrAccess(astNode);
    case 'closure':
      return buildClosure(astNode);
    default:
      return Assert.unreachable(astNode);
  }
}

function buildLiteral([
  token,
  { comments },
]: WithComments<LiteralToken>): FormatNode {
  const literalTextNode = buildLiteralToken(token);

  const leadingCommentsNodes: FormatNode[] = comments.leading.flatMap(
    (leadingComment) => [Text(`//${leadingComment}`), Line()]
  );

  return Nodes([
    ...(comments.leading.length ? [Line(), ...leadingCommentsNodes] : []),
    literalTextNode,
    ...(comments.trailing ? [Text(` //${comments.trailing}`)] : []),
  ]);
}

function buildLiteralToken({ val }: LiteralToken): FormatNode {
  switch (typeof val) {
    case 'string':
      return Text(
        `'${val
          .replaceAll('\\', '\\\\')
          .replaceAll('\n', '\\n')
          .replaceAll('\r', '\\r')
          .replaceAll('\t', '\\t')
          .replaceAll(`'`, `\\'`)}'`
      );
    case 'boolean':
      return Text(val ? 'true' : 'false');
    case 'number':
      return Text(val.toString());

    case 'object':
      if (val instanceof Date) {
        if (
          val.toISOString().length === 24 &&
          val.toISOString().slice(11, 23) === '00:00:00.000'
        ) {
          const YYYYMMDD = val.toISOString().slice(0, 10);
          return Text(`@${YYYYMMDD}`);
        } else {
          return Text(`cast('${val.toISOString()}', timestamp)`);
        }
      }
      return Assert.unreachable(val);

    default:
      return Assert.unreachable(val);
  }
}

/**
 * Builds a format Node tree from an EraQLAst Call Node.
 *
 * All tags are rendered as function calls
 * - tag(<expr>, "myTag")
 *
 * All operator calls rendered as infix if display is infix; else rendered as FCs.
 * - A op B
 *
 * All function calls are rendered as function calls
 * - fun(arg0, arg1, arg2, ...)
 *
 * Everything else rendered as pipes
 * - arg0 |> op(arg1, arg2, ...)
 */
function buildCall(callAstNode: Call): FormatNode {
  const {
    op: [op, opComments],
    args,
  } = callAstNode;

  switch (op.t) {
    case 'tag': {
      Assert.assert(args.length === 1);
      const leftExpr = assertExists(args[0]);
      return buildTag(leftExpr, op.tag);
    }

    case 'operator': {
      if (args.length == 2) {
        // Infix Operator
        const leftExpr = assertExists(args[0]);
        const rightExpr = assertExists(args[1]);
        return buildInfixOperatorCall(leftExpr, rightExpr, op.op);
      } else if (args.length == 1 && op.op === '!') {
        // Postfix Operator
        return Nodes([Text('('), buildNode(assertExists(args[0])), Text(')!')]);
      } else {
        // special form function call
        const infixFunctionIdentifier = INFIX_OPERATOR_TO_FC[op.op];

        if (infixFunctionIdentifier === undefined) {
          throw new Error(
            'Impossible. An operator call with (not 2 args) must have a function identifier'
          );
        }

        return buildFunctionCall(
          [{ t: 'symbol', sym: infixFunctionIdentifier }, opComments],
          args
        );
      }
    }

    default: {
      const _1: EraQlAst['t'] = op.t; // assert type

      type Arg = (typeof args)[number];

      switch (callAstNode.display) {
        case 'pipe': {
          Assert.assert(args.length >= 1);
          const [arg0, ...argRest] = args as [Arg, ...Arg[]];
          return buildPipeCall([op, opComments], arg0, argRest);
        }
        case 'fc':
          return buildFunctionCall([op, opComments], args);

        case 'template':
          return buildTemplateCall([op, opComments], args);

        case 'infix':
        case 'postfix':
        case null: {
          // assrt type
          const _unimplementedDisplayTypes: 'infix' | 'postfix' | null =
            callAstNode.display;
          return buildFunctionCall([op, opComments], args); // default unimplemented render
        }
        default:
          return Assert.unreachable(callAstNode.display);
      }
    }
  }
}

function buildAssocEntry(entry: AssocEntry): FormatNode {
  switch (entry.t) {
    case 'assoc-kv': {
      const {
        key: [key, _],
        val,
      } = entry;
      return Nodes([Text(`${escapeStructKey(key)}: `), buildNode(val)]);
    }

    case 'assoc-spread':
      return Nodes([Text('...'), buildNode(entry.val)]);

    default:
      return Assert.unreachable(entry);
  }
}

function buildAssoc(assocNode: Assoc): FormatNode {
  const headId = v4();

  const elemNodes: FormatNode[] = assocNode.entries.map(([entry, _meta]) =>
    buildAssocEntry(entry)
  );
  return Head(headId, [
    Text(elemNodes.length ? '{ ' : '{'),
    buildInnerSeries(headId, elemNodes, /* trailingSpace = */ true),
    LineOrNothing(),
    Text('}'),
  ]);
}

/**
 * Builds a format Node tree from an EraQLAst List Node.
 */
function buildList(listAstNode: List): FormatNode {
  const headId = v4();

  const elemNodes: FormatNode[] = listAstNode.elems.map(buildNode);

  return Head(headId, [
    Text('['),
    buildInnerSeries(headId, elemNodes),
    LineOrNothing(),
    Text(']'),
  ]);
}

/**
 * Builds the inner portion of a comma-separated list
 * (eg everything besides the enclosing parenthetical-grouping characters.)
 */
function buildInnerSeries(
  seriesHeadId: string,
  elemNodes: FormatNode[],
  trailingSpace = false // Whether to add a trailing space in the unrwap case
): FormatNode {
  // TODO: add a failing spec test if this were just LineOrNothing()
  const lineOrNothing = IfWrapAncestor(seriesHeadId, LineOrNothing(), Text(''));

  const nodes = elemNodes.flatMap((elemNode, idx, arr) => [
    lineOrNothing,
    elemNode,
    idx < arr.length - 1
      ? IfWrapAncestor(seriesHeadId, Text(','), Text(', ')) // When wrapped, no space after comma at end of line
      : IfWrapAncestor(seriesHeadId, Text(','), Text(trailingSpace ? ' ' : '')),
  ]);

  return Indent(nodes);
}

const buildLet = (letWithComments: WithComments<Let>): FormatNode => {
  const [
    {
      sym: [{ sym: symbolName }, _1], // comments lost
      bindingExpr,
      body,
    },
    {
      comments: { leading: leadingComments, trailing: _2 },
    }, // trailing comments lost
  ] = letWithComments;

  const leadingCommentsNodes: FormatNode[] = leadingComments.length
    ? [
        Line(),
        ...leadingComments.flatMap((leadingComment) => [
          Text(`//${leadingComment}`),
          Line(),
        ]),
      ]
    : [];

  return Nodes([
    ...leadingCommentsNodes,
    Text(`let ${symbolName} = `),
    buildNode(bindingExpr),
    Text(';'),
    Line(),
    buildNode(body),
  ]);
};

const buildAttrAccess = (attrAccess: AttrAccess): FormatNode => {
  const {
    in: base,
    attr: [{ name: attrName }, _2], // TODO capture comments
  } = attrAccess;

  return Nodes([buildNode(base), Text(`"${attrName}"`)]);
};

const buildClosure = (closure: Closure): FormatNode => {
  const {
    args: [argsList, _1], // TODO capture comments
    body,
  } = closure;

  const argsNodes = argsList.map(({ sym: [arg, _] }, idx, arr) =>
    Text(`${arg}${idx < arr.length - 1 ? ', ' : ''}`)
  );

  return Nodes([Text('|'), ...argsNodes, Text('| '), buildNode(body)]);
};

// TODO make a test case for failing Nodes([Text('('), buildNode(leftExpr), Text(` @'${tag}')`)]);
const buildTag = (leftExpr: WithComments<EraQlAst>, tag: string): FormatNode =>
  Nodes([Text('('), buildNode(leftExpr), Text(` @${tag})`)]);

const buildFunctionCall = (
  leftExpr: WithComments<EraQlAst>,
  args: readonly WithComments<EraQlAst>[]
): FormatNode => {
  const headId = v4();

  const argNodes = args.map(buildNode);

  /**
   * If just one argument, even one that is wrapped, don't add
   * a line break after the `(` or before the `)`
   *
   * eg:
   *
   * Where({
   *   abc: 123,
   *   def: 456,
   * })
   *
   * is better than
   *
   * Where(
   *   {
   *     abc: 123,
   *     def: 456,
   *   }
   * )
   */
  const argSeries =
    args.length === 1
      ? [assertExists(argNodes[0])]
      : [buildInnerSeries(headId, argNodes), LineOrNothing()];

  return Head(headId, [
    buildNode(leftExpr),
    Text('('),
    ...argSeries,
    Text(')'),
  ]);
};

const buildPipeCall = (
  leftExpr: WithComments<EraQlAst>,
  arg0: WithComments<EraQlAst>,
  argRest: readonly WithComments<EraQlAst>[]
): FormatNode =>
  Nodes([buildNode(arg0), Text(' |> '), buildFunctionCall(leftExpr, argRest)]);

const buildTemplateCall = (
  op: WithComments<EraQlAst>,
  args: readonly WithComments<EraQlAst>[]
) => {
  const opNode =
    op[0].t === 'symbol' && op[0].sym === 'format' ? Text('') : buildNode(op);

  const argNodes = args.flatMap(
    (arg) =>
      arg[0].t === 'literal' && typeof arg[0].val === 'string'
        ? [Text(arg[0].val.replaceAll('`', '\\`'))] // Escape backticks in template strings
        : [Text('{{ '), buildNode(arg), Text(' }}')] // TODO consider lifting comments outside the {{ }} grouping
  );

  return Nodes([opNode, Text('`'), ...argNodes, Text('`')]);
};
